import React from 'react';
import { ClickAwayListener, Popper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/system';

import {
  LINK_BLUE, LINK_BLUE_BG, MUTED, SANS_STACK,
  WARNING, WARNING_BG, WARNING_BODY, WARNING_HINT,
} from '../common/tokens';

const PROPOSED = {
  border: WARNING,
  text: WARNING_BODY,
  bg: WARNING_BG,
  label: WARNING_HINT,
};

function proposedNote(count) {
  return count === 1
    ? '1 tag is not public yet'
    : `${count} tags are not public yet`;
}

/**
 * The `+N` counter standing in for the tags a card has no room to show, and the
 * popper listing them.
 */
export default function MoreTagsChip({ tags, onTagOpen, isProposed }) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const proposedCount = isProposed ? tags.filter(isProposed).length : 0;

  React.useEffect(() => {
    if (!anchorEl) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAnchorEl(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [anchorEl]);

  const handleChipClick = (e) => {
    e.stopPropagation();
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={`Show ${tags.length} more tags`}
        aria-expanded={Boolean(anchorEl)}
        title={proposedCount > 0 ? proposedNote(proposedCount) : undefined}
        onClick={handleChipClick}
        sx={{
          margin: '2px',
          border: '1px solid #d1d5db',
          borderRadius: '10px',
          padding: '2px 8px',
          fontSize: '0.85rem',
          fontWeight: 400,
          fontFamily: SANS_STACK,
          letterSpacing: '0.5px',
          lineHeight: 1.4,
          color: '#3C3C3C',
          background: '#fff',
          cursor: 'pointer',
          flexShrink: 0,
          transition: 'background 0.12s, border-color 0.12s, color 0.12s',
          // Hidden tags can't show their own outline, so the counter carries the
          // flag — otherwise a proposal past the first chip goes unseen.
          ...(proposedCount > 0 && {
            border: `1px dashed ${PROPOSED.border}`,
            color: PROPOSED.text,
            background: PROPOSED.bg,
          }),
          '&:hover': {
            background: theme.palette.primary.tint,
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
        }}
      >
        +{tags.length}
      </Box>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1500 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Box
            role="dialog"
            sx={{
              width: 244,
              maxHeight: 320,
              overflowY: 'auto',
              background: '#fff',
              border: '2px solid #1a1a1a',
              borderRadius: '8px',
              boxShadow: '0 12px 32px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.15)',
              padding: '4px',
              animation: 'cl-pop-in 0.15s ease-out',
              '@keyframes cl-pop-in': {
                from: { opacity: 0, transform: 'translateY(-8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {tags.map((t) => {
              const proposed = Boolean(isProposed && isProposed(t));
              return (
                <Box
                  key={t.id}
                  component="button"
                  type="button"
                  title={proposed ? 'Not public yet' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onTagOpen) onTagOpen(t.id, t.name);
                    setAnchorEl(null);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: '6px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: 'none',
                    background: 'transparent',
                    fontFamily: SANS_STACK,
                    fontSize: '0.875rem',
                    color: proposed ? PROPOSED.text : '#1a1a1a',
                    textTransform: 'lowercase',
                    letterSpacing: '0.5px',
                    '&:hover': { background: LINK_BLUE_BG, color: LINK_BLUE },
                  }}
                >
                  <Box sx={{
                    flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t.name}
                  </Box>
                  {proposed && (
                    <Box sx={{
                      flexShrink: 0,
                      fontSize: 10,
                      fontWeight: 600,
                      letterSpacing: '.06em',
                      textTransform: 'uppercase',
                      color: PROPOSED.label,
                    }}>
                      proposed
                    </Box>
                  )}
                  {t.count != null && (
                    <Box sx={{ fontSize: 11, fontWeight: 700, color: MUTED, flexShrink: 0 }}>
                      {t.count.toLocaleString('en-US')}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
